// Pure helpers behind the patient Overview hub (patient pages switch): which rows count, which visits are
// "next" and "previous", and the one headline line a block shows. No data is invented here: every value is a
// field of a row the chart already fetched.
import { pmStatusBucket } from '@/lib/pm-appointment-status';
import type { GlyphKind } from '@/shared/ui/components/status-glyph-tokens';
import type { AppointmentForPatient } from '@/types/scheduling-api';
import { practiceNow } from '@/shared/lib/practice-time';

/** Statuses that close a visit whatever its date (the chart Appointments tab's rule, Flutter `appts_tab.dart`). */
const TERMINAL_STATUS = new Set(['Fulfilled', 'Cancelled', 'NoShow']);

/** Soft-deleted rows stay in several chart responses; strict equality on the tombstone, as the chart tabs do. */
export function liveRows<T extends { isDeleted?: boolean | null }>(rows: readonly T[] | null | undefined): T[] {
  return (rows ?? []).filter((r) => r.isDeleted !== true);
}

/** At most `n` non-empty labels, in order. */
export function firstLabels(labels: ReadonlyArray<string | null | undefined>, n = 3): string[] {
  return labels
    .map((l) => l?.trim() ?? '')
    .filter(Boolean)
    .slice(0, n);
}

function startOf(appt: AppointmentForPatient): Date | null {
  if (!appt.start) return null;
  const d = new Date(appt.start);
  return Number.isNaN(d.getTime()) ? null : d;
}

function isUpcoming(appt: AppointmentForPatient, now: Date): boolean {
  if (TERMINAL_STATUS.has(appt.status?.code ?? '')) return false;
  const start = startOf(appt);
  if (!start) return false;
  const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return startDay.getTime() >= today.getTime();
}

/** The earliest upcoming visit and the most recent earlier one. Blocks (no patient) are not visits. */
export function nextAndPreviousVisit(
  appointments: readonly AppointmentForPatient[] | null | undefined,
  now: Date = practiceNow(),
): {
  next: AppointmentForPatient | null;
  previous: AppointmentForPatient | null;
} {
  const visits = (appointments ?? []).filter((a) => a.isBlock !== true && startOf(a) !== null);
  const upcoming = visits.filter((a) => isUpcoming(a, now)).sort((a, b) => startOf(a)!.getTime() - startOf(b)!.getTime());
  const earlier = visits.filter((a) => !isUpcoming(a, now)).sort((a, b) => startOf(b)!.getTime() - startOf(a)!.getTime());
  return { next: upcoming[0] ?? null, previous: earlier[0] ?? null };
}

/** The glyph an appointment status draws (shape + colour; the label is written beside it). */
export function visitGlyph(statusCode: string | null | undefined): GlyphKind {
  const compact = (statusCode ?? '').toLowerCase().replace(/[\s_-]/g, '');
  if (compact === 'insession' || compact === 'inprogress') return 'full';
  switch (pmStatusBucket(statusCode)) {
    case 'arrived':
    case 'checkedIn':
      return 'half';
    case 'fulfilled':
      return 'done';
    case 'noShow':
      return 'alert';
    default:
      return 'empty';
  }
}

const DAY = 86_400_000;

/** "Today at 11:30", "Tomorrow at 09:00", "Yesterday", else "Mon 19 Oct at 10:00" (design log 13 Oct, next visit). */
export function visitWhen(iso: string | null | undefined, now: Date = practiceNow()): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  const dayStart = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((dayStart(d) - dayStart(now)) / DAY);
  if (diff === 0) return `Today at ${time}`;
  if (diff === 1) return `Tomorrow at ${time}`;
  if (diff === -1) return `Yesterday at ${time}`;
  const date = d.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' as const } : {}),
  });
  return `${date} at ${time}`;
}
